import React, { useState, useEffect } from "react";
import { Users, DollarSign, TrendingUp, Activity } from "lucide-react";
import {
  ref,
  onValue,
  query,
  orderByChild,
  limitToLast,
  get,
} from "firebase/database";
import { database, auth } from "../../../Config/firebase";
import Layout from "../Layout/Layout";
import StatCard from "../Components/StatCard";
import PerformanceChart from "../Components/PerformanceChart";
import ActivityFeed from "../Components/ActivityFeed";
import AffiliatesTable from "../Components/AffiliatesTable";
import MarketingTools from "../Components/MarketingTools";
import Footer from "../Components/Footer";
import AffiliatePreloader from "../Utils/AffiliatePreloader";

export default function DashboardPage() {
  const [userData, setUserData] = useState(null);
  const [affiliatesList, setAffiliatesList] = useState([]);
  const [performanceData, setPerformanceData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userID, setUserID] = useState(null);
  
  // Obtener datos del usuario actual y configurar escucha en tiempo real
  useEffect(() => {
    // Primero intentar obtener el usuario de Firebase Auth
    const unsubscribeAuth = auth.onAuthStateChanged((user) => {
      if (user) {
        setUserID(user.uid);
      } else {
        // Si no hay usuario en auth, intentar recuperar del localStorage
        const storedUserID = localStorage.getItem("userUUID");
        if (storedUserID) {
          setUserID(storedUserID);
        } else {
          setError("No hay usuario autenticado");
          setLoading(false);
        }
      }
    });

    return () => unsubscribeAuth();
  }, []);

  // Cargar los datos del usuario una vez que tenemos el userID
  useEffect(() => {
    if (!userID) return;

    const userRef = ref(database, `usuarios/${userID}`);

    // Establecer listener para actualización en tiempo real
    const unsubscribe = onValue(
      userRef,
      (snapshot) => {
        if (snapshot.exists()) {
          const data = snapshot.val();
          setUserData(data);

          // Procesar datos históricos de rendimiento del usuario
          processEarningsHistory(data);

          // Procesar los datos de afiliados desde userData.userAfiliates
          processUserAffiliates(data.userAfiliates);
        } else {
          setError("No se encontraron datos del usuario");
        }
        setLoading(false);
      },
      (error) => {
        setError(`Error al cargar datos: ${error.message}`);
        setLoading(false);
      }
    );

    // Limpiar listener cuando el componente se desmonte
    return () => unsubscribe();
  }, [userID]);

  // Procesar el historial de ganancias para el gráfico de rendimiento
  const processEarningsHistory = (userData) => {
    if (userData?.earningsHistory) {
      // Convertir el objeto de historial de ganancias a un formato adecuado para el gráfico
      const chartData = Object.entries(userData.earningsHistory).map(
        ([date, value]) => {
          // Formatear la fecha para mostrar solo el mes
          const month = new Date(date).toLocaleString("es-ES", {
            month: "short",
          });
          return {
            name: month.charAt(0).toUpperCase() + month.slice(1, 3),
            value: value,
          };
        }
      );

      // Ordenar por fecha (asumiendo que las fechas son en formato YYYY-MM-DD)
      chartData.sort((a, b) => {
        const dateA = new Date(
          Object.keys(userData.earningsHistory).find((key) =>
            new Date(key)
              .toLocaleString("es-ES", { month: "short" })
              .toLowerCase()
              .startsWith(a.name.toLowerCase())
          )
        );
        const dateB = new Date(
          Object.keys(userData.earningsHistory).find((key) =>
            new Date(key)
              .toLocaleString("es-ES", { month: "short" })
              .toLowerCase()
              .startsWith(b.name.toLowerCase())
          )
        );
        return dateA - dateB;
      });

      setPerformanceData(chartData);
    } else {
      // No hay historial de ganancias, mostrar datos vacíos
      setPerformanceData([]);
    }
  };

  // Procesar los datos de userAfiliates para el componente AffiliatesTable
  const processUserAffiliates = (userAffiliates) => {
    if (!userAffiliates) {
      setAffiliatesList([]);
      return;
    }

    // Convertir el objeto de afiliados a un array con el formato que espera AffiliatesTable
    const affiliatesArray = Object.entries(userAffiliates).map(
      ([id, affiliate]) => ({
        id,
        name: affiliate.name || "Usuario",
        email: affiliate.email || "No disponible",
        date: affiliate.createdAt
          ? new Date(affiliate.createdAt).toLocaleDateString()
          : "Fecha desconocida",
        status: affiliate.active ? "Activo" : "Inactivo",
        typeU: affiliate.typeU || "No especificado",
      })
    );

    setAffiliatesList(affiliatesArray);
  };

  // Manejo de estados de carga y error
  if (loading) {
    return <AffiliatePreloader />;
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-950 text-white">
        <p className="text-red-500 text-xl mb-4">Error: {error}</p>
        <button
          className="px-4 py-2 bg-blue-600 rounded hover:bg-blue-700 transition-colors"
          onClick={() => window.location.reload()}
        >
          Reintentar
        </button>
      </div>
    );
  }

  // Preparar datos de estadísticas basados en datos reales del usuario
  const stats = [
    {
      id: 1,
      name: "Afiliados",
      value: Object.keys(userData?.userAfiliates || {}).length.toLocaleString(),
      // change: userData?.statsChange?.afiliados || "0%",
      // increased: userData?.statsChange?.afiliados?.startsWith("+"),
      icon: <Users size={20} className="text-blue-500" />,
    },
    {
      id: 2,
      name: "Ganancias",
      value:
        userData?.ganancias?.saldo?.toLocaleString("es-CO", {
          style: "currency",
          currency: "COP",
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }) || "$0,00",
      // change: userData?.statsChange?.ganancias || "0%",
      // increased: userData?.statsChange?.ganancias?.startsWith("+"),
      icon: <DollarSign size={20} className="text-green-500" />,
    },
    {
      id: 3,
      name: "Cuentas Vip",
      value: Object.values(userData?.userAfiliates || {})
        .filter((u) => u.typeU === "Cuenta Vip")
        .length.toLocaleString(),
      change: userData?.statsChange?.cuentasVip || "0%",
      increased: userData?.statsChange?.cuentasVip?.startsWith("+"),
      icon: <TrendingUp size={20} className="text-purple-500" />,
    },
    {
      id: 4,
      name: "Cuentas Gratis",
      value: Object.values(userData?.userAfiliates || {})
        .filter((u) => u.typeU === "Cuenta Gratis")
        .length.toLocaleString(),
      change: userData?.statsChange?.cuentasGratis || "0%",
      increased: userData?.statsChange?.cuentasGratis?.startsWith("+"),
      icon: <Activity size={20} className="text-yellow-500" />,
    },
  ];

  return (
    <Layout userData={userData}>
      <div className="mb-6">
        <h2 className="text-xl sm:text-2xl font-bold mb-1">
          Bienvenido, {userData?.name || userData?.publicName || "Usuario"}
        </h2>
        <p className="text-gray-400">
          {userData?.typeU === "Afiliada" ? "Afiliada" : "Afiliado"} desde{" "}
          {userData?.createdAt
            ? new Date(userData.createdAt).toLocaleDateString()
            : "fecha desconocida"}
        </p>
        {userData?.biography && (
          <p className="text-gray-400 mt-1 italic">"{userData.biography}"</p>
        )}
      </div>

      {/* Fila de estadísticas */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {stats.map((stat) => (
          <StatCard key={stat.id} stat={stat} />
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        {/* Gráfico de rendimiento */}
        <div className="lg:col-span-2">
          <div className="bg-gray-800 rounded-lg p-6 shadow-lg">
            <h3 className="text-xl font-semibold text-white mb-4">
              Rendimiento Mensual
            </h3>
            {performanceData.length > 0 ? (
              <PerformanceChart chartData={performanceData} />
            ) : (
              <div className="text-center py-8 text-gray-400">
                <p>No hay datos de rendimiento disponibles.</p>
              </div>
            )}
          </div>
        </div>

        {/* Feed de actividad */}
        <div>
          <ActivityFeed userId={userID} activities={userData?.activities} />
        </div>
      </div>

      {/* Herramientas de Marketing */}
      <div className="mb-6">
        <MarketingTools userData={userData} />
      </div>

      {/* Tabla de afiliados */}
      <div className="mb-6">
        <AffiliatesTable
          affiliates={affiliatesList}
          referralCode={userData?.referralCode}
        />
      </div>

      {/* Footer */}
      <Footer />
    </Layout>
  );
}
